/* eslint-disable @typescript-eslint/naming-convention */
import classNames from 'classnames';
import React from 'react';
import { Link } from 'react-router-dom';
import { IWithReactRouterProps } from '../../mixins/router/withReactRouter';
import { useTranslate } from '../../mixins/translate/translate';
import { fetchJson } from '../../store/fetch';
import { Roles } from '../../utils/enums/Roles';
import { appContext } from '../appContext';
import { Content, LeftTray, LeftTrayItem, LeftTrayList, Panel, PanelBody, PanelRouterLink, Topbar, TopbarLeft, TopbarRight, Tray } from '../elements';
import { IElement } from '../interfaces/IElement';
import ObjectTree, { IObjectTreeSelection } from '../Surveys/Common/ObjectTree';
import { ICategory } from '../Surveys/Interfaces/ICategory';
import { useUserHasRole } from '../utils/authorization/useUserHasRole';
import ObjectAddEdit from './ObjectAddEdit';
import ObjectCompositionEdit from './ObjectCompositionEdit';
import { ObjectEditDocuments } from './ObjectEditDocuments';
import { ObjectMetaData } from './ObjectMetaData';

// eslint-disable-next-line no-shadow
enum TabName {
    details = 'edit',
    composition = 'editcomposition',
    documents = 'documents',
    metadata = 'metadata',
    objectTypeFields = 'objectTypeFields',
}

export const ObjectTemplate = (props: IWithReactRouterProps) => {
    const currentAppContext = React.useContext(appContext.context);
    const { userHasRole } = useUserHasRole();
    const isClient = currentAppContext.isClient || userHasRole([Roles.Administrator]);
    const objectId = props.match.params.id;
    const translate = useTranslate();

    const [objectName, setObjectName] = React.useState<string>('');
    const [elements, setElements] = React.useState<IElement[]>([]);
    const [categories, setCategories] = React.useState<ICategory[]>([]);

    const [objectTreeSelection, setObjectTreeSelection] = React.useState<IObjectTreeSelection>({
        isArchiveSelected: false,
    });

    const tabs = [
        ...[isClient ? { name: TabName.details, text: translate('survey.basicData') } : {}],
        ...[isClient ? { name: TabName.composition, text: translate('common.composition') } : {}],
        { name: TabName.documents, text: translate('survey.documents') },
        { name: TabName.metadata, text: translate('survey.additionalData') },
    ];

    const currentTabName = tabs.find((item) => item.name === props.match.params.tab) ? props.match.params.tab : TabName.documents;

    const showObjectTree = currentTabName === TabName.documents;

    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    const hasSelection = () =>
        objectTreeSelection.elementGroupName ??
        objectTreeSelection.elementId ??
        objectTreeSelection.isArchiveSelected ??
        objectTreeSelection.manifestationId ??
        objectTreeSelection.unitId;

    React.useEffect(() => {
        const loadElements = async () => {
            const categoriesResult = await fetchJson(`/api/objects/${objectId}/categories`, 'GET');
            setCategories(categoriesResult.json);
            const elementsResult = await fetchJson(`/api/objects/${objectId}/elements`, 'GET');
            setElements(elementsResult.json);
        };
        void loadElements();
    }, [currentTabName, objectId]);

    React.useEffect(() => {
        const loadObject = async () => {
            const objectResult = await fetchJson(`/api/objects/${objectId}`, 'GET');
            setObjectName(objectResult.json.name);
        };
        void loadObject();
    }, [objectId]);

    return (
        <div>
            <Topbar>
                <TopbarLeft>
                    <ul className="nav nav-list nav-list-topbar pull-left">
                        <li className="pr20">
                            <h4>
                                <span className="fa fa-building pr10"></span>
                                {translate('common.editItem', translate('common.object'))}
                            </h4>
                        </li>
                        {tabs.map((tab, i) => (
                            <li key={i} className={classNames({ active: currentTabName === tab.name })}>
                                <Link to={`/objects/${props.match.params.id}/${tab.name}`}>{tab.text}</Link>
                            </li>
                        ))}
                    </ul>
                </TopbarLeft>
                <TopbarRight>
                    <PanelRouterLink to={`/objects/${objectId}`} text={translate('object.showObject')} icon="fa fa-search" small={true} />
                </TopbarRight>
            </Topbar>
            <Content>
                <LeftTray>
                    <LeftTrayList>
                        <LeftTrayItem>{objectName}</LeftTrayItem>
                    </LeftTrayList>

                    {showObjectTree && elements && (
                        <ObjectTree
                            objectId={objectId}
                            categories={categories}
                            elements={elements}
                            isLoading={false}
                            isUploading={false}
                            location={props.location}
                            tab={currentTabName}
                            elementGroups={[]}
                            onUpdateSelection={(value) => setObjectTreeSelection(value)}
                            selection={objectTreeSelection}
                            options={{
                                hasElementsGroups: false,
                                archiveLinkAvailable: true,
                                hideEditObjectBreakdownButton: true,
                                archiveMapName: translate('common.general'),
                                hiddeElements: true,
                            }}
                            filterOnElementGroups={false}
                        />
                    )}
                </LeftTray>

                {currentTabName === TabName.details && isClient && <ObjectAddEdit />}
                {currentTabName === TabName.metadata && <ObjectMetaData objectId={objectId} />}
                {currentTabName === TabName.composition && isClient && <ObjectCompositionEdit {...props} />}
                {currentTabName === TabName.documents && (
                    <>
                        {(hasSelection() && <ObjectEditDocuments objectTreeSelection={objectTreeSelection} objectId={objectId} />) || (
                            <Tray>
                                <Panel>
                                    <PanelBody></PanelBody>
                                </Panel>
                            </Tray>
                        )}
                    </>
                )}
            </Content>
        </div>
    );
};
